// Notes editor — live-rendering markdown for the scratchpad.
//
// Public entry point: mountNotesEditor({ root, initialMarkdown, onChange }).
// The renderer's notes controller calls this once per textarea slot
// (scene + script) and treats the returned handle as the
// canonical write/read surface — no textarea, no edit/preview swap.
// Input rules transform markdown syntax into formatted blocks as the
// writer types (Notion-style), and `getMarkdown()` returns the
// canonical markdown string for persistence.
//
// Why ProseMirror? It's already in the bundle for the screenplay
// editor; the patterns (schema + input rules + appendTransaction) are
// well-trodden. A textarea-with-overlay alternative would require
// reinventing selection / undo / IME / paste handling.

import { EditorState, TextSelection, Plugin } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import {
  InputRule,
  inputRules,
  wrappingInputRule,
  textblockTypeInputRule,
} from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import {
  baseKeymap,
  chainCommands,
  setBlockType,
  toggleMark,
  splitBlock,
  newlineInCode,
  createParagraphNear,
  liftEmptyBlock,
} from 'prosemirror-commands';
import { history, undo, redo } from 'prosemirror-history';
import { canSplit } from 'prosemirror-transform';

import { notesSchema as S } from './schema.js';
import { markdownToDoc, docToMarkdown } from './markdown.js';

// =========================================================================
// Block input rules (typed at the start of a paragraph)
// =========================================================================

function blockInputRules() {
  return [
    // ATX headings: `# ` / `## ` / `### ` at the start of a paragraph.
    textblockTypeInputRule(/^(#{1,3})\s$/, S.nodes.heading, (match) => ({
      level: match[1].length,
    })),

    // Blockquote: `> ` at the start of a paragraph.
    wrappingInputRule(/^>\s$/, S.nodes.blockquote),

    // Bullet list: `- ` or `* ` at the start of a paragraph.
    wrappingInputRule(/^[-*]\s$/, S.nodes.bullet_list),

    // Ordered list: `1. ` at the start of a paragraph. PM's wrapping
    // rule takes a third arg to set the start number; we extract it
    // so writers can begin a list at any number.
    wrappingInputRule(
      /^(\d+)\.\s$/,
      S.nodes.ordered_list,
      (match) => ({ order: Number(match[1]) }),
      (match, node) => node.childCount + node.attrs.order === Number(match[1]),
    ),

    // Fenced code block: type ``` then space (no language).
    textblockTypeInputRule(/^```\s$/, S.nodes.code_block),

    // Horizontal rule: --- at start of an empty paragraph, followed
    // by Enter — PM input rules fire on the trigger char, so we
    // accept the third dash.
    new InputRule(/^(?:---|\*\*\*)$/, (state, _match, start, end) => {
      const tr = state.tr;
      tr.replaceRangeWith(start - 1, end, S.nodes.horizontal_rule.create());
      return tr;
    }),
  ];
}

// =========================================================================
// Inline mark input rules (the live-rendering bit — typed marker
// pairs collapse into formatted text as soon as the writer closes
// the pair).
// =========================================================================

function inlineMarkRule(regex, markType, attrsFromMatch) {
  return new InputRule(regex, (state, match, start, end) => {
    const attrs = attrsFromMatch ? attrsFromMatch(match) : null;
    const mark = markType.create(attrs);
    const innerText = match[1] || '';
    if (innerText.length === 0) return null;
    const tr = state.tr;
    // Replace `[start, end)` (the marker pair + inner text + trailing
    // trigger char) with the inner text carrying the mark.
    tr.replaceRangeWith(start, end, S.text(innerText, [mark]));
    // After replace, place cursor immediately after the marked text
    // with no marks active — so the writer can keep typing in normal
    // text without dragging strong/em forward.
    const after = start + innerText.length;
    tr.setSelection(TextSelection.create(tr.doc, after));
    tr.removeStoredMark(mark);
    return tr;
  });
}

function inlineInputRules() {
  return [
    // Strong: **text** or __text__
    inlineMarkRule(/(?:\*\*|__)([^\s*_][^*_]*[^\s*_]|\S)(?:\*\*|__)$/, S.marks.strong),

    // Em: *text* or _text_ (one-character version). Lookbehind to
    // avoid matching the closing `*` of a `**bold**` pair as the
    // opener of an `*em*`.
    inlineMarkRule(/(?:^|[^*_])([*_])([^\s*_][^*_]*[^\s*_]|\S)\1$/, S.marks.em, () => null),

    // Inline code: `text`
    inlineMarkRule(/`([^`\n]+)`$/, S.marks.code),

    // Link: [text](href)
    new InputRule(/\[([^\]]+)\]\(([^)]+)\)$/, (state, match, start, end) => {
      const linkText = match[1];
      const href = match[2].trim();
      const linkMark = S.marks.link.create({ href });
      const tr = state.tr;
      tr.replaceRangeWith(start, end, S.text(linkText, [linkMark]));
      const after = start + linkText.length;
      tr.setSelection(TextSelection.create(tr.doc, after));
      tr.removeStoredMark(linkMark);
      return tr;
    }),

    // Autolink: a bare http(s) URL followed by space or punctuation
    // becomes a clickable link. Fires on the trigger char (space,
    // comma, period, etc.) so the link "closes" the moment the writer
    // moves on. Without this, a pasted URL like
    // `https://x.com/cold_fire7/...` stays as plain text — no click
    // handler, not navigable. Same gate the click handler uses:
    // http(s) only, never javascript: or other schemes.
    new InputRule(/(\bhttps?:\/\/\S+?)([\s,.;!?])$/, (state, match, start, end) => {
      const url = match[1];
      const trailing = match[2];
      const linkMark = S.marks.link.create({ href: url });
      const tr = state.tr;
      tr.replaceWith(start, end, [S.text(url, [linkMark]), S.text(trailing)]);
      const after = start + url.length + trailing.length;
      tr.setSelection(TextSelection.create(tr.doc, after));
      tr.removeStoredMark(linkMark);
      return tr;
    }),
  ];
}

// =========================================================================
// Keymap helpers (Enter / Backspace handling around lists +
// headings, plus the usual bold/italic/code shortcuts).
// =========================================================================

// Exit an empty list item by lifting back into a paragraph. Without
// this, Enter inside an empty bullet just adds another empty bullet
// and the writer has no way out.
function exitEmptyListItem(state, dispatch) {
  const { $from } = state.selection;
  if ($from.parent.type !== S.nodes.paragraph) return false;
  if ($from.parent.content.size !== 0) return false;
  const item = $from.node($from.depth - 1);
  if (!item || item.type !== S.nodes.list_item) return false;
  const list = $from.node($from.depth - 2);
  if (!list || (list.type !== S.nodes.bullet_list && list.type !== S.nodes.ordered_list))
    return false;
  // Lift the empty paragraph out of the list. Reuse liftEmptyBlock —
  // it walks up to the nearest acceptable parent.
  return liftEmptyBlock(state, dispatch);
}

// Inside a non-empty list item, Enter splits the list_item itself
// (creating a sibling) rather than the paragraph inside it. Without
// this, a generic splitBlock would create two paragraphs in one
// list_item — the writer would see one giant bullet instead of two
// separate ones.
//
// Mirrors prosemirror-schema-list's splitListItem so we don't have
// to add the package as a dep for this single function.
function splitListItem(state, dispatch) {
  const { $from, $to } = state.selection;
  if (!state.selection.empty) return false;
  if ($from.depth < 2) return false;
  const grandParent = $from.node(-1);
  if (grandParent.type !== S.nodes.list_item) return false;
  // Empty paragraph inside the last item of a list — exitEmptyListItem
  // handles this case before us via chainCommands.
  if ($from.parent.content.size === 0 && grandParent.childCount === $from.indexAfter(-1)) {
    return false;
  }
  const nextType = $to.pos === $from.end() ? grandParent.contentMatchAt(0).defaultType : null;
  const tr = state.tr.delete($from.pos, $to.pos);
  const types = nextType ? [null, { type: nextType }] : undefined;
  if (!canSplit(tr.doc, $from.pos, 2, types)) return false;
  if (dispatch) dispatch(tr.split($from.pos, 2, types).scrollIntoView());
  return true;
}

// Convert heading → paragraph on Enter at the end of the line (the
// writer has finished the heading text, the next line should be body
// copy). Matches typical doc-editor behavior.
function paragraphAfterHeading(state, dispatch) {
  const { $from } = state.selection;
  if ($from.parent.type !== S.nodes.heading) return false;
  if ($from.pos !== $from.end()) return false;
  const tr = state.tr.split($from.pos, 1, [{ type: S.nodes.paragraph }]);
  if (dispatch) dispatch(tr);
  return true;
}

function noteKeymap() {
  return {
    'Mod-z': undo,
    'Mod-Shift-z': redo,
    'Mod-y': redo,
    'Mod-b': toggleMark(S.marks.strong),
    'Mod-i': toggleMark(S.marks.em),
    'Mod-`': toggleMark(S.marks.code),
    Enter: chainCommands(
      newlineInCode,
      exitEmptyListItem,
      paragraphAfterHeading,
      splitListItem,
      createParagraphNear,
      liftEmptyBlock,
      splitBlock,
    ),
    // Backspace at start of heading/blockquote/list converts back to
    // a normal paragraph — chainCommands tries each in turn.
    Backspace: chainCommands((state, dispatch) => {
      const { $from } = state.selection;
      if (!state.selection.empty || $from.parentOffset !== 0) return false;
      if ($from.parent.type === S.nodes.heading) {
        return setBlockType(S.nodes.paragraph)(state, dispatch);
      }
      return false;
    }, baseKeymap.Backspace),
  };
}

// =========================================================================
// Mount
// =========================================================================

export function mountNotesEditor({ root, initialMarkdown, onChange, placeholder } = {}) {
  if (!root) throw new Error('mountNotesEditor: root element required');

  const state = EditorState.create({
    doc: markdownToDoc(initialMarkdown || ''),
    plugins: [
      history(),
      keymap(noteKeymap()),
      keymap(baseKeymap),
      inputRules({ rules: [...blockInputRules(), ...inlineInputRules()] }),
      placeholderPlugin(placeholder || ''),
    ],
  });

  const EXTERNAL_META = 'lastslate-notes-external';

  // Inside a contenteditable, clicking an <a> just moves the caret —
  // standard PM behavior, but useless for a writer who pasted a
  // reference URL and wants to actually visit it. Intercept the
  // click on the editor root and route http(s) links through the
  // Electron shell so they open in the default browser. Same gate
  // the chat-side markdown renderer uses (markdown.js:295) — never
  // navigate the renderer itself, never trust non-http schemes.
  const onLinkClick = (event) => {
    let node = event.target;
    while (node && node !== root) {
      if (node.tagName === 'A' && node.getAttribute('href')) {
        const href = node.getAttribute('href');
        if (/^https?:\/\//i.test(href)) {
          event.preventDefault();
          // Use `openUserLink`, NOT `openExternal`. openExternal is
          // gated by a host allowlist (lastslate.com, github.com, …)
          // — meant for app-controlled links like Settings / Billing.
          // Writers pasting any URL into their notes (x.com, a
          // research paper, a Google Doc) need the un-allowlisted
          // channel. Both go through shell.openExternal in main; the
          // user-link path keeps the http(s) gate to block
          // javascript:/file: URIs.
          const opener =
            window.screenplay &&
            (typeof window.screenplay.openUserLink === 'function'
              ? window.screenplay.openUserLink
              : window.screenplay.openExternal);
          if (typeof opener === 'function') {
            opener(href);
          }
          root.dispatchEvent(
            new CustomEvent('lastslate-notes-link-open', { detail: { href }, bubbles: true }),
          );
        }
        return;
      }
      node = node.parentNode;
    }
  };
  root.addEventListener('click', onLinkClick);

  // Strict URL test for paste handling. We only short-circuit PM's
  // default paste when the clipboard is *exactly* a URL — no leading
  // text, no trailing text. A multi-line paste with a URL inside
  // falls through to PM's normal paste so the writer doesn't lose
  // surrounding content.
  const URL_RE = /^https?:\/\/\S+$/i;

  const view = new EditorView(root, {
    state,
    handlePaste(view, event) {
      const cdata = event.clipboardData;
      if (!cdata) return false;
      const text = (cdata.getData('text/plain') || '').trim();
      if (!text || !URL_RE.test(text)) return false;
      const { from, to, empty } = view.state.selection;
      const linkMark = S.marks.link.create({ href: text });
      if (!empty) {
        // Writer highlighted some text first → wrap it in the link.
        // This is the "highlight + paste URL" gesture Notion / Bear /
        // every doc editor supports; without this, the selected text
        // gets replaced by the raw URL string.
        const tr = view.state.tr.addMark(from, to, linkMark);
        view.dispatch(tr);
      } else {
        // Bare URL paste with no selection → insert as a self-titled
        // link so it's immediately clickable, instead of plain text
        // that no one can navigate to.
        const tr = view.state.tr.insert(from, S.text(text, [linkMark]));
        view.dispatch(tr);
      }
      return true; // PM skips its own paste handling
    },
    dispatchTransaction(tr) {
      const next = view.state.apply(tr);
      view.updateState(next);
      // External updates (setMarkdown loading saved content from
      // disk, or re-keying after a heading migration) are NOT user
      // edits and must not re-fire onChange — otherwise the autosave
      // loop would write the just-loaded content back under whatever
      // foreign key was stale at the time, duplicating notes.
      if (tr.docChanged && typeof onChange === 'function' && !tr.getMeta(EXTERNAL_META)) {
        try {
          onChange(docToMarkdown(next.doc));
        } catch (err) {
          console.warn('[notes-editor] onChange threw:', err && err.message);
        }
      }
    },
  });

  return {
    view,
    getMarkdown() {
      return docToMarkdown(view.state.doc);
    },
    setMarkdown(text) {
      // Only swap if the incoming markdown differs from what we'd
      // serialize. Without this, every external load() call (e.g.
      // after a scene-change race) would wipe the writer's pending
      // edits.
      const current = docToMarkdown(view.state.doc);
      const incoming = typeof text === 'string' ? text : '';
      if (current === incoming) return;
      const newDoc = markdownToDoc(incoming);
      const tr = view.state.tr.replaceWith(0, view.state.doc.content.size, newDoc.content);
      tr.setMeta('addToHistory', false);
      tr.setMeta(EXTERNAL_META, true);
      view.dispatch(tr);
    },
    focus() {
      view.focus();
      const end = view.state.doc.content.size;
      const tr = view.state.tr.setSelection(TextSelection.create(view.state.doc, end));
      view.dispatch(tr);
    },
    destroy() {
      root.removeEventListener('click', onLinkClick);
      view.destroy();
    },
  };
}

// Placeholder: when the doc is empty (single empty paragraph), a
// CSS pseudo-element renders the placeholder string. We mark the
// view with a `data-empty` attribute the CSS selectors hook onto.
function placeholderPlugin(text) {
  return new Plugin({
    view(view) {
      const update = () => {
        const isEmpty =
          view.state.doc.childCount === 1 &&
          view.state.doc.firstChild.type === S.nodes.paragraph &&
          view.state.doc.firstChild.content.size === 0;
        view.dom.toggleAttribute('data-empty', isEmpty);
        if (text) view.dom.setAttribute('data-placeholder', text);
      };
      update();
      return { update };
    },
  });
}
